import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";
import UrduKeyboard from "../Components/UrduKeyboard";
import LoadingScreen from "../Components/LoadingScreen";
import { useBackspaceNavigation } from "../hooks/useBackspaceNavigation";

function CreateRecovery() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRecoveryId = searchParams.get('edit');
  const isEditMode = !!editRecoveryId;
  const urlLang = searchParams.get('lang');

  // Backspace navigation to dashboard
  useBackspaceNavigation();

  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [language, setLanguage] = useState('ur'); // Default to Urdu
  const [companyLanguage, setCompanyLanguage] = useState('ur');

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };
  const [ledgerParties, setLedgerParties] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Recovery list states
  const [recoveries, setRecoveries] = useState([]);
  const [loadingRecoveries, setLoadingRecoveries] = useState(false);
  const [selectedRecoveries, setSelectedRecoveries] = useState([]);
  const [showBalance, setShowBalance] = useState(false);

  const todayLocal = new Date().toLocaleDateString('en-CA');
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: todayLocal,
    dateTo: todayLocal
  });
  const [showFilterSearchUrduKeyboard, setShowFilterSearchUrduKeyboard] = useState(false);
  const filterSearchInputRef = useRef(null);

  const [formData, setFormData] = useState({
    serialNumber: '',
    customerPartyId: '',
    customerName: '',
    customerNameUrdu: '',
    customerVehicleNumber: '',
    customerPhone: '',
    recoveryAmount: '',
    paymentMethod: '',
    bankId: '',
    description: '',
    descriptionUrdu: '',
    date: new Date().toLocaleDateString('en-CA')
  });

  const [focusedFields, setFocusedFields] = useState({});
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerUrduKeyboard, setShowCustomerUrduKeyboard] = useState(false);
  const customerDropdownRef = useRef(null);
  const customerSearchInputRef = useRef(null);

  // Recovery rows state (rows-based form like CreateBillPart2)
  const [recoveryRows, setRecoveryRows] = useState([
    {
      id: Date.now(),
      customerPartyId: '',
      customerName: '',
      customerNameUrdu: '',
      customerSearchTerm: '',
      showDropdown: false,
      recoveryAmount: ''
    }
  ]);
  const [recoveryRowUrduKeyboard, setRecoveryRowUrduKeyboard] = useState(null);
  const recoveryCustomerDropdownRefs = useRef({});
  const recoveryCustomerInputRefs = useRef({});
  const recoveryAmountInputRefs = useRef({});
  const [isSavingRecovery, setIsSavingRecovery] = useState(false);
  const [recoveryRowHighlightedIndex, setRecoveryRowHighlightedIndex] = useState({}); // Track highlighted index for each row
  const recoveryCustomerListRefs = useRef({}); // Refs for customer list items in each row

  // Refs for fields to handle Enter key navigation
  const fieldRefs = useRef({});

  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      setFilters(prev => ({
        ...prev,
        dateFrom: dateParam,
        dateTo: dateParam
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          fetchCompanyData(),
          fetchLedgerParties(),
          fetchPaymentMethods(),
          fetchRecoveries()
        ]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();

    if (editRecoveryId) {
      fetchRecoveryData(editRecoveryId);
    }
  }, [editRecoveryId]);

  // Fetch recoveries when filters change (with debounce)
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRecoveries();
    }, 500); // 500ms debounce

    return () => clearTimeout(handler);
  }, [filters]);

  // Auto-close Urdu keyboard when English character is typed in filter search
  // Auto-open when field becomes empty
  useEffect(() => {
    if (filters.search === undefined || filters.search === null) return;

    const value = String(filters.search);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowFilterSearchUrduKeyboard(false);
    } else if (value.trim() === '' && showFilterSearchUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (filterSearchInputRef.current && filterSearchInputRef.current === document.activeElement) {
        setShowFilterSearchUrduKeyboard(true);
      }
    }
  }, [filters.search, showFilterSearchUrduKeyboard]);

  // Handle Urdu keyboard input for filter search field
  const handleFilterSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setFilters(prev => ({ ...prev, search: prev.search ? prev.search.slice(0, -1) : '' }));
    } else {
      setFilters(prev => ({ ...prev, search: (prev.search || '') + key }));
    }
  };

  // Toggle Urdu keyboard on filter search input click
  // Open keyboard if field is empty, otherwise toggle
  const handleFilterSearchInputClick = () => {
    if (!filters.search || filters.search.trim() === '') {
      // If field is empty, open keyboard
      setShowFilterSearchUrduKeyboard(true);
    } else {
      // Otherwise toggle
      setShowFilterSearchUrduKeyboard(prev => !prev);
    }
  };

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      // Close recovery dropdowns
      recoveryRows.forEach(row => {
        const dropdownRef = recoveryCustomerDropdownRefs.current[row.id];
        if (dropdownRef && !dropdownRef.contains(event.target)) {
          setRecoveryRows(prev =>
            prev.map(r => r.id === row.id ? { ...r, showDropdown: false } : r)
          );
        }
      });
    };

    const hasOpenDropdowns = showCustomerDropdown || recoveryRows.some(row => row.showDropdown);

    if (hasOpenDropdowns) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown, recoveryRows]);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompany(response.data.company);
      // Allow forcing language via URL (?lang=en or ?lang=ur)
      const dashboardType = response.data.company?.dashboardType || response.data.company?.dashboard_type || 'main';
      const isPart2 = dashboardType === 'part2' || dashboardType === 'dashboard-part2' || dashboardType === 'part-2' || dashboardType === 'ur' || dashboardType === 'urdu';

      const companyLang = urlLang || (isPart2 ? 'ur' : (response.data.company?.language || 'ur'));
      setLanguage(companyLang);
      setCompanyLanguage(companyLang);

      // Auto-generate serial number only if not in edit mode
      if (!editRecoveryId) {
        // Fetch only the latest recovery to get the next serial number (optimization)
        const recoveriesResponse = await api.get("/recovery/company/all?limit=10", {
          headers: { Authorization: `Bearer ${token}` }
        });

        let nextSerial = 1;
        if (recoveriesResponse.data.recoveries && recoveriesResponse.data.recoveries.length > 0) {
          const validSerials = recoveriesResponse.data.recoveries
            .map(recovery => {
              const sn = recovery.serialNumber?.toString().trim() || '';
              const num = parseInt(sn, 10);
              return !isNaN(num) && num > 0 ? num : null;
            })
            .filter(num => num !== null);

          if (validSerials.length > 0) {
            const maxSerial = Math.max(...validSerials);
            nextSerial = maxSerial + 1;
          } else {
            nextSerial = recoveriesResponse.data.recoveries.length + 1;
          }
        }

        setFormData(prev => ({ ...prev, serialNumber: String(nextSerial) }));
      }
    } catch (error) {
      console.error("Fetch company error:", error);
      toast.error("کمپنی ڈیٹا لوڈ کرنے میں ناکام");
    }
  };

  const fetchRecoveries = async () => {
    try {
      setLoadingRecoveries(true);
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Build query parameters
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      // Increased limit for the list view
      params.append('limit', '500');

      const response = await api.get(`/recovery/company/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recoveriesData = response.data.recoveries || [];
      setRecoveries(recoveriesData);
    } catch (error) {
      console.error("Fetch recoveries error:", error);
    } finally {
      setLoadingRecoveries(false);
    }
  };

  // Navigate to edit recovery
  const handleEditRecovery = (recoveryId) => {
    navigate(`/recovery/create?edit=${recoveryId}`);
  };

  // Delete recovery and refresh list
  const handleDeleteRecovery = async (recoveryId) => {
    const confirmMsg = language === 'ur' ? 'کیا آپ واقعی یہ ریکوری حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this recovery?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      await api.delete(`/recovery/company/${recoveryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'ur' ? 'ریکوری حذف ہو گئی' : 'Recovery deleted');
      fetchRecoveries();
      // Remove from selected list if it was selected
      setSelectedRecoveries(prev => prev.filter(id => id !== recoveryId));
    } catch (error) {
      console.error("Delete recovery error:", error);
      toast.error(language === 'ur' ? 'ریکوری حذف کرنے میں ناکام' : 'Failed to delete recovery');
    }
  };

  // Handle select/deselect recovery
  const handleSelectRecovery = (recoveryId) => {
    setSelectedRecoveries(prev => {
      if (prev.includes(recoveryId)) {
        return prev.filter(id => id !== recoveryId);
      } else {
        return [...prev, recoveryId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedRecoveries.length === recoveries.length) {
      setSelectedRecoveries([]);
    } else {
      setSelectedRecoveries(recoveries.map(r => r._id));
    }
  };

  // Bulk delete selected recoveries
  const handleBulkDelete = async () => {
    if (selectedRecoveries.length === 0) {
      toast.warning(language === 'ur' ? 'براہ کرم کم از کم ایک ریکوری منتخب کریں' : 'Please select at least one recovery');
      return;
    }

    const confirmMsg = language === 'ur'
      ? `کیا آپ واقعی ${selectedRecoveries.length} ریکوریاں حذف کرنا چاہتے ہیں؟`
      : `Are you sure you want to delete ${selectedRecoveries.length} recoveries?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Delete all selected recoveries
      const deletePromises = selectedRecoveries.map(recoveryId =>
        api.delete(`/recovery/company/${recoveryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      await Promise.all(deletePromises);
      toast.success(language === 'ur' ? `${selectedRecoveries.length} ریکوریاں حذف ہو گئیں` : `${selectedRecoveries.length} recoveries deleted successfully`);
      setSelectedRecoveries([]);
      await fetchRecoveries();
    } catch (error) {
      console.error("Error deleting recoveries:", error);
      toast.error(language === 'ur' ? 'ریکوریاں حذف نہیں ہو سکیں' : 'Failed to delete recoveries');
    }
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/ledger/parties?type=customer", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-method/active");
      setPaymentMethods(response.data.paymentMethods || []);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      // Fallback to default methods if API fails
      setPaymentMethods([
        { name: 'نقد رقم', nameUrdu: 'نقد رقم' },
        { name: 'Easypaisa', nameUrdu: 'ایزی پیسہ' },
        { name: 'Punjab Bank', nameUrdu: 'پنجاب بینک' }
      ]);
    }
  };

  const fetchRecoveryData = async (recoveryId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get(`/recovery/company/${recoveryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recovery = response.data.recovery;
      if (!recovery) {
        toast.error(language === 'ur' ? 'ریکوری نہیں ملی' : 'Recovery not found');
        navigate('/company/dashboard');
        return;
      }

      // Populate form with recovery data
      setFormData({
        serialNumber: recovery.serialNumber || '',
        customerPartyId: recovery.customerPartyId || '',
        customerName: recovery.customerName || '',
        customerNameUrdu: recovery.customerNameUrdu || '',
        customerVehicleNumber: recovery.customerVehicleNumber || '',
        customerPhone: recovery.customerPhone || '',
        recoveryAmount: recovery.recoveryAmount || '',
        paymentMethod: recovery.paymentMethod || '',
        bankId: recovery.bankId || '',
        description: recovery.description || '',
        descriptionUrdu: recovery.descriptionUrdu || '',
        date: recovery.date ? new Date(recovery.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA')
      });

      // Populate recoveryRows for edit mode
      setRecoveryRows([{
        id: Date.now(),
        customerPartyId: recovery.customerPartyId || '',
        customerName: recovery.customerName || '',
        customerNameUrdu: recovery.customerNameUrdu || '',
        customerSearchTerm: recovery.customerNameUrdu || recovery.customerName || '',
        showDropdown: false,
        recoveryAmount: recovery.recoveryAmount || '',
        paymentMethod: recovery.paymentMethod || '',
        bankId: recovery.bankId || '',
        description: recovery.description || '',
        descriptionUrdu: recovery.descriptionUrdu || ''
      }]);
    } catch (error) {
      console.error("Fetch recovery error:", error);
      toast.error(language === 'ur' ? 'ریکوری ڈیٹا لوڈ کرنے میں ناکام' : 'Failed to load recovery data');
      navigate('/company/dashboard');
    } finally {
      setIsLoading(false);
    }
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle Urdu keyboard input for customer search
  const handleCustomerSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setCustomerSearchTerm(prev => prev ? prev.slice(0, -1) : '');
    } else {
      setCustomerSearchTerm(prev => (prev || '') + key);
      setShowCustomerDropdown(true);
    }
  };

  // Toggle Urdu keyboard on customer search input click
  const handleCustomerSearchInputClick = () => {
    setShowCustomerUrduKeyboard(prev => !prev);
  };

  const handleCustomerSelect = (partyId) => {
    const party = ledgerParties.find(p => p._id === partyId);
    if (party) {
      setFormData(prev => ({
        ...prev,
        customerPartyId: partyId,
        customerName: party.name,
        customerNameUrdu: party.nameUrdu || party.name,
        customerPhone: party.phone || ''
      }));
    }
  };

  const handleFocus = (field) => {
    setFocusedFields(prev => ({ ...prev, [field]: true }));

    // Clear 0 values
    const value = formData[field];
    if (value === 0 || value === '0') {
      handleInputChange(field, '');
    }
  };

  const handleBlur = (field) => {
    setFocusedFields(prev => {
      const newState = { ...prev };
      delete newState[field];
      return newState;
    });

    // Restore 0 if empty
    const value = formData[field];
    if (value === '' || value === null || value === undefined) {
      if (field === 'recoveryAmount') {
        handleInputChange(field, 0);
      } else {
        handleInputChange(field, '');
      }
    }
  };

  const getDisplayValue = (field) => {
    const value = formData[field];
    if (focusedFields[field] && (value === 0 || value === '0')) {
      return '';
    }
    return value || '';
  };

  // Handle Enter key to move to next field
  const handleKeyDown = (e, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      let nextField = null;

      // Define field order
      switch (currentField) {
        case 'date':
          nextField = 'customerPartyId';
          break;
        case 'customerPartyId':
          nextField = 'customerName';
          break;
        case 'customerName':
          nextField = 'customerVehicleNumber';
          break;
        case 'customerVehicleNumber':
          nextField = 'customerPhone';
          break;
        case 'customerPhone':
          nextField = 'recoveryAmount';
          break;
        case 'recoveryAmount':
          nextField = 'paymentMethod';
          break;
        case 'paymentMethod':
          nextField = 'description';
          break;
        case 'description':
          // Last field - submit form or do nothing
          return;
        default:
          return;
      }

      // Focus on next field
      if (nextField && fieldRefs.current[nextField]) {
        setTimeout(() => {
          fieldRefs.current[nextField].focus();
          // For select fields, try to open dropdown
          if (fieldRefs.current[nextField].tagName === 'SELECT') {
            fieldRefs.current[nextField].focus();
          }
        }, 10);
      }
    }
  };

  // Recovery form handlers (rows-based)
  const handleAddRecoveryRow = () => {
    setRecoveryRows(prev => [
      ...prev,
      {
        id: Date.now(),
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: '',
        customerSearchTerm: '',
        showDropdown: false,
        recoveryAmount: ''
      }
    ]);
  };

  const handleRemoveRecoveryRow = (rowId) => {
    if (recoveryRows.length > 1) {
      setRecoveryRows(prev => prev.filter(row => row.id !== rowId));
    } else {
      toast.warning(language === 'ur' ? 'کم از کم ایک قطار ضروری ہے' : 'At least one row is required');
    }
  };

  const handleRecoveryRowChange = (rowId, field, value) => {
    setRecoveryRows(prev =>
      prev.map(row => {
        if (row.id !== rowId) return row;
        return { ...row, [field]: value };
      })
    );
  };

  const handleRecoveryCustomerSearch = (rowId, value) => {
    setRecoveryRows(prev =>
      prev.map(row => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          customerSearchTerm: value,
          showDropdown: true
        };
      })
    );
    // Reset highlighted index when search changes
    setRecoveryRowHighlightedIndex(prev => ({
      ...prev,
      [rowId]: -1
    }));
  };

  const handleRecoveryCustomerSelect = (rowId, partyId) => {
    const party = ledgerParties.find(p => p._id === partyId);
    if (party) {
      setRecoveryRows(prev =>
        prev.map(row => {
          if (row.id !== rowId) return row;
          return {
            ...row,
            customerPartyId: partyId,
            customerName: party.name,
            customerNameUrdu: party.nameUrdu || party.name,
            customerSearchTerm: '',
            showDropdown: false
          };
        })
      );
      setRecoveryRowUrduKeyboard(null);
    }
  };

  const handleRecoveryCustomerUrduKeyPress = (key, rowId) => {
    if (key === 'backspace') {
      setRecoveryRows(prev =>
        prev.map(row => {
          if (row.id !== rowId) return row;
          return {
            ...row,
            customerSearchTerm: row.customerSearchTerm ? row.customerSearchTerm.slice(0, -1) : '',
            showDropdown: true
          };
        })
      );
    } else {
      setRecoveryRows(prev =>
        prev.map(row => {
          if (row.id !== rowId) return row;
          return {
            ...row,
            customerSearchTerm: (row.customerSearchTerm || '') + key,
            showDropdown: true
          };
        })
      );
    }
  };

  const handleSaveRecovery = async () => {
    // Validate all rows
    const invalidRows = recoveryRows.filter(row => !row.customerPartyId || !row.recoveryAmount);
    if (invalidRows.length > 0) {
      toast.error(language === 'ur' ? 'براہ کرم تمام قطاروں میں گاہک اور رقم منتخب کریں' : 'Please select customer and amount for all rows');
      return;
    }

    setIsSavingRecovery(true);
    try {
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(language === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        setIsSavingRecovery(false);
        return;
      }

      if (isEditMode && editRecoveryId) {
        // Update existing recovery
        const row = recoveryRows[0]; // In edit mode, there's only one row
        const recoveryPayload = {
          serialNumber: formData.serialNumber || '',
          customerPartyId: row.customerPartyId,
          customerName: row.customerName,
          customerNameUrdu: row.customerNameUrdu,
          customerVehicleNumber: formData.customerVehicleNumber || '',
          customerPhone: formData.customerPhone || '',
          recoveryAmount: parseFloat(row.recoveryAmount) || 0,
          paymentMethod: row.paymentMethod || formData.paymentMethod || '',
          bankId: row.bankId || formData.bankId || '',
          description: row.description || formData.description || '',
          descriptionUrdu: row.descriptionUrdu || formData.descriptionUrdu || '',
          date: formData.date
        };

        await api.put(`/recovery/company/${editRecoveryId}`, recoveryPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success(language === 'ur' ? 'ریکوری کامیابی سے اپ ڈیٹ ہو گئی!' : 'Recovery updated successfully!');

        // Navigate back to dashboard
        navigate('/company/dashboard-part2?tab=recoveries');
      } else {
        // Create new recoveries
        // Get next serial number
        const recoveriesResponse = await api.get("/recovery/company/all", {
          headers: { Authorization: `Bearer ${token}` }
        });
        let nextSerial = 1;
        if (recoveriesResponse.data.recoveries && recoveriesResponse.data.recoveries.length > 0) {
          const validSerials = recoveriesResponse.data.recoveries
            .map(recovery => {
              const sn = recovery.serialNumber?.toString().trim() || '';
              const num = parseInt(sn, 10);
              return !isNaN(num) && num > 0 ? num : null;
            })
            .filter(num => num !== null);
          if (validSerials.length > 0) {
            const maxSerial = Math.max(...validSerials);
            nextSerial = maxSerial + 1;
          } else {
            nextSerial = recoveriesResponse.data.recoveries.length + 1;
          }
        }

        // Save all recovery rows
        const savePromises = recoveryRows.map((row, index) => {
          const recoveryPayload = {
            serialNumber: String(nextSerial + index),
            customerPartyId: row.customerPartyId,
            customerName: row.customerName,
            customerNameUrdu: row.customerNameUrdu,
            recoveryAmount: parseFloat(row.recoveryAmount) || 0,
            paymentMethod: row.paymentMethod || '',
            bankId: row.bankId || '',
            description: row.description || '',
            descriptionUrdu: row.descriptionUrdu || '',
            date: formData.date
          };
          return api.post("/recovery/company/create", recoveryPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        });

        await Promise.all(savePromises);

        toast.success(language === 'ur' ? `${recoveryRows.length} ریکوریاں بن گئیں!` : `${recoveryRows.length} recoveries created successfully!`);

        // Reset recovery rows to single empty row
        setRecoveryRows([{
          id: Date.now(),
          customerPartyId: '',
          customerName: '',
          customerNameUrdu: '',
          customerSearchTerm: '',
          showDropdown: false,
          recoveryAmount: ''
        }]);
        setRecoveryRowUrduKeyboard(null);
      }

      // Refresh recovery list
      await fetchRecoveries();
    } catch (error) {
      console.error('Save recovery error:', error);
      toast.error(error.response?.data?.message || (language === 'ur' ? 'ریکوری محفوظ نہیں ہو سکی' : 'Failed to save recovery'));
    } finally {
      setIsSavingRecovery(false);
    }
  };

  if (isLoading) {
    return (
      <LoadingScreen
        companyLanguage={companyLanguage}
        message={companyLanguage === "ur" ? "ریکوری فارم لوڈ ہو رہا ہے..." : "Loading recovery form..."}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? (language === 'ur' ? 'ریکوری تبدیل کریں' : 'Edit Recovery') : (language === 'ur' ? 'نیا ریکوری بنائیں' : 'Create Recovery')}</title>
      </Helmet>

      {/* Company Header - Fixed at Top */}
      {/* Company Header Hidden */}
      {/* {company && (
        <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage} 
          onLogout={handleLogout} 
        />
      )} */}

      <div className="min-h-screen bg-gray-100 pt-[6px] pb-8 px-4" dir="rtl">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white shadow-lg rounded-lg p-6 mb-6 relative">
            <button
              onClick={() => navigate('/company/dashboard-part2')}
              className="absolute left-6 top-6 px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all flex items-center gap-2"
              title={language === 'ur' ? 'ڈیش بورڈ' : 'Dashboard'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className={language === 'ur' ? 'font-urdu' : ''} style={language === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {language === 'ur' ? 'ڈیش بورڈ' : 'Dashboard'}
              </span>
            </button>
            <h1 className={`text-2xl font-bold text-center mb-2 ${language === 'ur' ? 'font-urdu' : ''}`}>
              {isEditMode
                ? (language === 'ur' ? 'ریکوری تبدیل کریں' : 'Edit Recovery')
                : (language === 'ur' ? 'ریکوری رسید' : 'Recovery Receipt')
              }
            </h1>
          </div>

          {/* Top Section: Filters (Left) + Form (Right) */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Left Side - Filters */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className={`text-xl font-bold mb-4 ${language === 'ur' ? 'font-urdu' : ''}`}>
                {language === 'ur' ? 'فلٹرز' : 'Filters'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'تلاش' : 'Search'}
                  </label>
                  <input
                    ref={filterSearchInputRef}
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onClick={handleFilterSearchInputClick}
                    onFocus={() => {
                      // Open Urdu keyboard on focus if field is empty
                      if (!filters.search || filters.search.trim() === '') {
                        setShowFilterSearchUrduKeyboard(true);
                      }
                    }}
                    placeholder={language === 'ur' ? 'نام یا نمبر تلاش کریں' : 'Search by name or number'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded text-gray-700 ${language === 'ur' ? 'font-urdu text-right' : ''}`}
                    style={language === 'ur' ? {
                      fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                      lineHeight: '1.8'
                    } : {}}
                    dir={language === 'ur' ? 'rtl' : 'ltr'}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'تاریخ سے' : 'Date From'}
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="w-full px-3 py-1 border border-gray-300 rounded text-gray-700"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'تاریخ تک' : 'Date To'}
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="w-full px-3 py-1 border border-gray-300 rounded text-gray-700"
                  />
                </div>
                <button
                  onClick={() => setFilters({
                    search: '',
                    dateFrom: todayLocal,
                    dateTo: todayLocal
                  })}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded transition-all"
                >
                  {language === 'ur' ? 'فلٹرز صاف کریں' : 'Clear Filters'}
                </button>
              </div>
            </div>

            {/* Right Side - Recovery Form */}
            <div className="md:col-span-2 bg-white shadow-lg rounded-lg p-6">
              {/* Date Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'ur' ? 'تاریخ:' : 'Date:'}
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="w-full px-3 py-1 border border-black rounded text-gray-700"
                />
              </div>

              {/* Recovery Form Table */}
              <div className="mb-6">
                <h2 className={`text-2xl font-bold text-purple-800 mb-6 text-center ${language === 'ur' ? 'font-urdu' : ''}`}>
                  {language === 'ur' ? 'ریکوری فارم' : 'Recovery Form'}
                </h2>

                <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '800px', minHeight: '400px' }}>
                  <table className="w-full border-collapse border border-black">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="border border-black px-4 py-1.5 text-right">{language === 'ur' ? 'گاہک کا نام' : 'Customer Name'}</th>
                        <th className="border border-black px-4 py-1.5 text-right">{language === 'ur' ? 'ریکوری کی رقم' : 'Recovery Amount'}</th>
                        <th className="border border-black px-4 py-1.5 text-center">{language === 'ur' ? 'عمل' : 'Action'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recoveryRows.map((row) => (
                        <tr key={row.id}>
                          {/* Customer Name */}
                          <td className="border border-black px-2 py-1">
                            <div className="relative" ref={(el) => recoveryCustomerDropdownRefs.current[row.id] = el}>
                              <input
                                ref={(el) => recoveryCustomerInputRefs.current[row.id] = el}
                                type="text"
                                value={row.customerPartyId
                                  ? (ledgerParties.find(p => p._id === row.customerPartyId)?.nameUrdu ||
                                    ledgerParties.find(p => p._id === row.customerPartyId)?.name || '')
                                  : row.customerSearchTerm}
                                onChange={(e) => handleRecoveryCustomerSearch(row.id, e.target.value)}
                                onKeyDown={(e) => {
                                  const filteredParties = ledgerParties.filter(party => {
                                    const searchLower = row.customerSearchTerm.toLowerCase();
                                    const nameLower = (party.name || '').toLowerCase();
                                    const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                                    return party.type === 'customer' && (nameLower.includes(searchLower) || nameUrduLower.includes(searchLower));
                                  });

                                  if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    if (filteredParties.length > 0) {
                                      const currentIndex = recoveryRowHighlightedIndex[row.id] || -1;
                                      const newIndex = currentIndex < filteredParties.length - 1
                                        ? currentIndex + 1
                                        : 0;
                                      setRecoveryRowHighlightedIndex(prev => ({
                                        ...prev,
                                        [row.id]: newIndex
                                      }));
                                      // Scroll highlighted item into view
                                      setTimeout(() => {
                                        const itemRef = recoveryCustomerListRefs.current[`${row.id}_${filteredParties[newIndex]?._id}`];
                                        if (itemRef) {
                                          itemRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                        }
                                      }, 0);
                                    }
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    if (filteredParties.length > 0) {
                                      const currentIndex = recoveryRowHighlightedIndex[row.id] || -1;
                                      const newIndex = currentIndex > 0
                                        ? currentIndex - 1
                                        : filteredParties.length - 1;
                                      setRecoveryRowHighlightedIndex(prev => ({
                                        ...prev,
                                        [row.id]: newIndex
                                      }));
                                      // Scroll highlighted item into view
                                      setTimeout(() => {
                                        const itemRef = recoveryCustomerListRefs.current[`${row.id}_${filteredParties[newIndex]?._id}`];
                                        if (itemRef) {
                                          itemRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                        }
                                      }, 0);
                                    }
                                  } else if (e.key === 'Enter' && (recoveryRowHighlightedIndex[row.id] ?? -1) >= 0 && filteredParties[recoveryRowHighlightedIndex[row.id]]) {
                                    e.preventDefault();
                                    handleRecoveryCustomerSelect(row.id, filteredParties[recoveryRowHighlightedIndex[row.id]]._id);
                                    setRecoveryRowHighlightedIndex(prev => ({
                                      ...prev,
                                      [row.id]: -1
                                    }));
                                  }
                                }}
                                onClick={() => {
                                  setRecoveryRowUrduKeyboard(row.id);
                                  handleRecoveryRowChange(row.id, 'showDropdown', true);
                                }}
                                onFocus={() => {
                                  setRecoveryRowUrduKeyboard(row.id);
                                  handleRecoveryRowChange(row.id, 'showDropdown', true);
                                }}
                                className="w-full px-2 py-1 border border-black rounded text-sm"
                                placeholder={language === 'ur' ? 'گاہک تلاش کریں...' : 'Search customer...'}
                                style={{
                                  fontFamily: language === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                                  lineHeight: language === 'ur' ? '1.8' : '1.5'
                                }}
                              />
                              {row.showDropdown && (
                                <div
                                  className="absolute z-[9999] w-full mt-1 bg-white border border-black rounded shadow-lg overflow-y-auto"
                                  dir="rtl"
                                  style={{
                                    right: 0,
                                    top: '100%',
                                    maxHeight: '300px',
                                    minHeight: '100px'
                                  }}
                                >
                                  {ledgerParties.filter(party => {
                                    const searchLower = row.customerSearchTerm.toLowerCase();
                                    const nameLower = (party.name || '').toLowerCase();
                                    const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                                    return party.type === 'customer' && (nameLower.includes(searchLower) || nameUrduLower.includes(searchLower));
                                  }).map((party, index) => {
                                    const isHighlighted = (recoveryRowHighlightedIndex[row.id] ?? -1) === index;
                                    return (
                                      <div
                                        key={party._id}
                                        ref={(el) => {
                                          if (el) {
                                            recoveryCustomerListRefs.current[`${row.id}_${party._id}`] = el;
                                          }
                                        }}
                                        onClick={() => {
                                          handleRecoveryCustomerSelect(row.id, party._id);
                                          setRecoveryRowHighlightedIndex(prev => ({
                                            ...prev,
                                            [row.id]: -1
                                          }));
                                        }}
                                        className={`px-3 py-2 cursor-pointer text-gray-700 text-right border-b border-gray-100 last:border-b-0 ${isHighlighted
                                          ? 'bg-blue-100 border-2 border-blue-300'
                                          : 'hover:bg-gray-100'
                                          }`}
                                      >
                                        {party.nameUrdu || party.name}
                                      </div>
                                    );
                                  })}
                                  {ledgerParties.filter(party => {
                                    const searchLower = row.customerSearchTerm.toLowerCase();
                                    const nameLower = (party.name || '').toLowerCase();
                                    const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                                    return party.type === 'customer' && (nameLower.includes(searchLower) || nameUrduLower.includes(searchLower));
                                  }).length === 0 && row.customerSearchTerm && (
                                      <div className="px-3 py-2 text-gray-500 text-center">
                                        {language === 'ur' ? 'کوئی گاہک نہیں ملا' : 'No customer found'}
                                      </div>
                                    )}
                                </div>
                              )}
                              {recoveryRowUrduKeyboard === row.id && (
                                <UrduKeyboard
                                  isVisible={recoveryRowUrduKeyboard === row.id}
                                  onKeyPress={(key) => handleRecoveryCustomerUrduKeyPress(key, row.id)}
                                  onClose={() => setRecoveryRowUrduKeyboard(null)}
                                  inputRef={recoveryCustomerInputRefs.current[row.id]}
                                />
                              )}
                            </div>
                          </td>

                          {/* Recovery Amount */}
                          <td className="border border-black px-2 py-1">
                            <input
                              ref={(el) => recoveryAmountInputRefs.current[row.id] = el}
                              type="text"
                              value={row.recoveryAmount}
                              onChange={(e) => handleRecoveryRowChange(row.id, 'recoveryAmount', e.target.value.replace(/[^0-9.]/g, ''))}
                              className="w-full px-2 py-1 border border-black rounded text-right text-sm"
                              placeholder=""
                            />
                          </td>

                          {/* Actions */}
                          <td className="border border-black px-2 py-1 text-center">
                            {recoveryRows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveRecoveryRow(row.id)}
                                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                              >
                                {language === 'ur' ? 'ختم کریں' : 'Remove'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 flex gap-2">
                    {!isEditMode && (
                      <button
                        type="button"
                        onClick={handleAddRecoveryRow}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                      >
                        + {language === 'ur' ? 'نیا قطر شامل کریں' : 'Add New Row'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveRecovery}
                      disabled={isSavingRecovery}
                      className={`px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded ${isSavingRecovery ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                      {isSavingRecovery
                        ? (language === 'ur' ? 'محفوظ ہو رہا ہے...' : 'Saving...')
                        : (isEditMode
                          ? (language === 'ur' ? 'ریکوری اپ ڈیٹ کریں' : 'Update Recovery')
                          : (language === 'ur' ? 'ریکوری محفوظ کریں' : 'Save Recovery')
                        )
                      }
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section - Recovery Lists */}
          <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${language === 'ur' ? 'font-urdu' : ''}`}>
                {language === 'ur' ? 'ریکوری کی فہرست' : 'Recovery List'}
                <span className={`text-sm font-normal text-gray-600 ${language === 'ur' ? 'mr-2' : 'ml-2'}`}>
                  ({recoveries.length})
                </span>
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 text-sm font-semibold ${showBalance
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {language === 'ur' ? (showBalance ? 'بیلنس چھپائیں' : 'بیلنس دکھائیں') : (showBalance ? 'Hide Balance' : 'Show Balance')}
                </button>
                {selectedRecoveries.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {language === 'ur' ? `منتخب شدہ حذف کریں (${selectedRecoveries.length})` : `Delete Selected (${selectedRecoveries.length})`}
                  </button>
                )}
              </div>
            </div>

            {loadingRecoveries ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                <p className={`mt-4 text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                  {language === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
                </p>
              </div>
            ) : recoveries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className={language === 'ur' ? 'font-urdu' : ''}>
                  {language === 'ur' ? 'کوئی ریکوری نہیں ملی' : 'No recoveries found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] border-collapse border border-gray-300">
                  <thead className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 text-white">
                    <tr>
                      <th className={`px-4 py-1.5 text-right font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'نمبر شمار' : 'Sr. No.'}
                      </th>
                      <th className={`px-4 py-1.5 text-center font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedRecoveries.length === recoveries.length && recoveries.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title={language === 'ur' ? 'تمام منتخب کریں' : 'Select All'}
                        />
                      </th>
                      <th className={`px-4 py-1.5 text-right font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'تاریخ' : 'Date'}
                      </th>
                      <th className={`px-4 py-1.5 text-right font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'کسٹمر' : 'Customer'}
                      </th>
                      <th className={`px-4 py-1.5 text-right font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'وصولی رقم' : 'Recovery Amount'}
                      </th>
                      {showBalance && (
                        <th className={`px-4 py-1.5 text-right font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                          {language === 'ur' ? 'بقایا رقم' : 'Balance'}
                        </th>
                      )}
                      <th className={`px-4 py-1.5 text-center font-bold border border-green-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'عمل' : 'Action'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recoveries.map((recovery, index) => (
                      <tr
                        key={recovery._id}
                        className={`border-b border-gray-200 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedRecoveries.includes(recovery._id) ? 'bg-blue-50' : ''}`}
                      >
                        <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                          {index + 1}
                        </td>
                        <td className="px-4 py-1.5 text-center border border-gray-300">
                          <input
                            type="checkbox"
                            checked={selectedRecoveries.includes(recovery._id)}
                            onChange={() => handleSelectRecovery(recovery._id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                          {new Date(recovery.date).toLocaleDateString(language === 'ur' ? 'ur-PK' : 'en-GB')}
                        </td>
                        <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                          {recovery.customerNameUrdu || recovery.customerName || '-'}
                        </td>
                        <td className="px-4 py-1.5 text-right font-semibold text-green-700 border border-gray-300">
                          {parseFloat(recovery.recoveryAmount || 0).toLocaleString()}
                        </td>
                        {showBalance && (
                          <td className="px-4 py-1.5 text-right font-semibold text-orange-700 border border-gray-300">
                            {(() => {
                              const party = ledgerParties.find(p => p._id === recovery.customerPartyId);
                              return party ? (party.currentBalance || 0).toLocaleString() : '0';
                            })()}
                          </td>
                        )}
                        <td className="px-4 py-1.5 text-center border border-gray-300">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/recovery/view/${recovery._id}`)}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-all"
                            >
                              {language === 'ur' ? 'ریکوری' : 'Recovery'}
                            </button>
                            <button
                              onClick={() => handleEditRecovery(recovery._id)}
                              className="bg-brand-button hover:bg-yellow-400 text-gray-900 px-3 py-1 rounded text-sm transition-all"
                            >
                              {language === 'ur' ? 'تبدیل کریں' : 'Edit'}
                            </button>
                            <button
                              onClick={() => handleDeleteRecovery(recovery._id)}
                              className="bg-brand-alert hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-all"
                            >
                              {language === 'ur' ? 'ختم کریں' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td colSpan={4} className={`px-4 py-3 text-right font-bold border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'کل' : 'Total'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-700 border border-gray-300">
                        {recoveries.reduce((sum, r) => sum + parseFloat(r.recoveryAmount || 0), 0).toLocaleString()}
                      </td>
                      <td className="border border-gray-300" colSpan={showBalance ? 2 : 1}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Urdu Keyboard for Filter Search */}
      {showFilterSearchUrduKeyboard && (
        <UrduKeyboard
          isVisible={showFilterSearchUrduKeyboard}
          onKeyPress={handleFilterSearchUrduKeyPress}
          onClose={() => setShowFilterSearchUrduKeyboard(false)}
          inputRef={filterSearchInputRef}
        />
      )}
    </>
  );
}

export default CreateRecovery;

